import React, { useRef, useState } from 'react';
import { Upload, Trash2, Film, Image as ImageIcon, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, SectionTitle, inputCls, btnPrimary, btnGhost, btnAccent } from './MlmShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'producto', label: 'Vender producto' },
  { id: 'reclutar', label: 'Atraer socios' },
  { id: 'testimonio', label: 'Testimonio' },
  { id: 'educativo', label: 'Educativo' },
  { id: 'estilo_vida', label: 'Estilo de vida' },
];

export const MlmMedia = ({ workspace, api, busy }) => {
  const { authFetch } = useAuth();
  const { media } = workspace;
  const [tags, setTags] = useState('');
  const [progress, setProgress] = useState('');
  const [videoModal, setVideoModal] = useState(null);
  const [videoForm, setVideoForm] = useState({ goal: 'producto', script: '', voice_over: true });
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setProgress(`Subiendo 0/${files.length}…`);
    let ok = 0;
    for (let i = 0; i < files.length; i += 1) {
      const fd = new FormData();
      fd.append('files', files[i]); fd.append('tags', tags);
      try {
        const res = await authFetch(`${API}/mlm/media`, { method: 'POST', body: fd });
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || 'Error');
        ok += 1;
      } catch (err) { toast.error(`${files[i].name}: ${err.message}`); }
      setProgress(`Subiendo ${i + 1}/${files.length}…`);
    }
    setProgress('');
    if (ok) toast.success(`${ok} archivo(s) guardados en la biblioteca`);
    await api('/workspace', {}, { silent: false, key: 'reload-media' });
    if (inputRef.current) inputRef.current.value = '';
  };

  const makeVideo = async () => {
    try {
      await api(`/media/${videoModal.id}/to-video`, { method: 'POST', body: JSON.stringify(videoForm) }, { success: 'Vídeo creado con voz. Ya está en tu biblioteca para TikTok/Reels', key: `video-${videoModal.id}` });
      setVideoModal(null);
      setVideoForm({ goal: 'producto', script: '', voice_over: true });
    } catch { /* toast ya mostrado */ }
  };

  return (
    <div className="space-y-5">
      {videoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="mlm-video-modal" onClick={(e) => e.target === e.currentTarget && setVideoModal(null)}>
          <div className="w-full max-w-lg rounded-[24px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#E2001A]">Foto → Vídeo</p>
                <h3 className="mt-1 text-xl font-black text-[#062A5E]">Convertir en vídeo con voz</h3>
                <p className="mt-1 text-sm text-slate-500">Aynara crea un vídeo vertical (9:16) con locución en español a partir de esta foto, listo para TikTok y Reels.</p>
              </div>
              <button type="button" onClick={() => setVideoModal(null)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100" data-testid="mlm-video-modal-close"><X className="h-5 w-5" /></button>
            </div>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${videoModal.path}`} alt={videoModal.title} className="mt-4 h-40 w-full rounded-xl object-cover" />
            <div className="mt-4 space-y-3">
              <select value={videoForm.goal} onChange={(e) => setVideoForm({ ...videoForm, goal: e.target.value })} className={inputCls} data-testid="mlm-video-goal">{GOALS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select>
              <textarea rows={3} placeholder="Guion (opcional). Si lo dejas vacío, Aynara lo escribe por ti." value={videoForm.script} onChange={(e) => setVideoForm({ ...videoForm, script: e.target.value })} className={inputCls} data-testid="mlm-video-script" />
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={videoForm.voice_over} onChange={(e) => setVideoForm({ ...videoForm, voice_over: e.target.checked })} data-testid="mlm-video-voice" />Añadir voz de Aynara (recomendado)</label>
            </div>
            <button type="button" onClick={makeVideo} disabled={busy === `video-${videoModal.id}`} className={`${btnAccent} mt-4 w-full`} data-testid="mlm-video-generate"><Sparkles className="h-4 w-4" />{busy === `video-${videoModal.id}` ? 'Generando vídeo (30-60s)…' : 'Crear vídeo con Aynara'}</button>
          </div>
        </div>
      )}
      <Card testId="mlm-media-upload">
        <SectionTitle kicker="Biblioteca de medios" title="Fotos y vídeos oficiales PM / FitLine" subtitle="Sube el material promocional de la empresa. Aynara lo adjunta a los posts del calendario, lo convierte en vídeos con voz y lo publica en Instagram, Facebook y TikTok." />
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files)); }} className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#0B3F8C]/40 bg-[#f1f5fb] px-4 py-8 text-center text-sm text-slate-600 sm:col-span-2" data-testid="mlm-media-dropzone">
            <Upload className="h-6 w-6 text-[#0B3F8C]" />
            <span className="font-semibold text-[#062A5E]">Arrastra aquí o haz clic para elegir</span>
            <span className="text-xs text-slate-500">JPG, PNG, WEBP, GIF, MP4, MOV · hasta 200 MB por archivo · varios a la vez</span>
            <input ref={inputRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload(Array.from(e.target.files))} data-testid="mlm-media-input" />
          </label>
          <div className="space-y-2">
            <input placeholder="Etiquetas (coma): Activize, Evento" value={tags} onChange={(e) => setTags(e.target.value)} className={inputCls} data-testid="mlm-media-tags" />
            <button type="button" onClick={() => inputRef.current?.click()} disabled={!!progress} className={`${btnPrimary} w-full`} data-testid="mlm-media-choose"><Upload className="h-4 w-4" />{progress || 'Elegir archivos'}</button>
            <p className="text-xs text-slate-500">{media.length} archivos · {media.filter((m) => m.kind === 'video').length} vídeos</p>
          </div>
        </div>
      </Card>
      <Card testId="mlm-media-grid">
        <SectionTitle kicker="Biblioteca" title={`${media.length} medios`} />
        <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {media.length === 0 && <p className="text-sm text-slate-500 sm:col-span-5" data-testid="mlm-media-empty">Aún no hay medios. Sube las fotos y vídeos oficiales de PM/FitLine.</p>}
          {media.map((m) => (
            <figure key={m.id} className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-[#fbfcfe]" data-testid={`mlm-media-item-${m.id}`}>
              {m.kind === 'video' ? <video src={`${process.env.REACT_APP_BACKEND_URL}${m.path}`} className="h-36 w-full object-cover" muted playsInline preload="metadata" /> : <img src={`${process.env.REACT_APP_BACKEND_URL}${m.path}`} alt={m.title} className="h-36 w-full object-cover" loading="lazy" />}
              <figcaption className="p-2 text-xs">
                <p className="truncate font-bold text-[#062A5E]">{m.kind === 'video' ? <Film className="mr-1 inline h-3 w-3" /> : <ImageIcon className="mr-1 inline h-3 w-3" />}{m.title}</p>
                <p className="text-slate-500">{(m.size / 1024 / 1024).toFixed(1)} MB{m.tags?.length ? ` · ${m.tags.join(', ')}` : ''}</p>
              </figcaption>
              <button type="button" onClick={() => window.confirm('¿Eliminar este medio?') && api(`/media/${m.id}`, { method: 'DELETE' }, { success: 'Medio eliminado', key: `media-del-${m.id}` })} className={`${btnGhost} absolute right-2 top-2 !p-1.5 opacity-0 transition group-hover:opacity-100`} data-testid={`mlm-media-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-[#E2001A]" /></button>
              {m.kind === 'image' && <button type="button" onClick={() => { setVideoModal(m); setVideoForm({ goal: 'producto', script: '', voice_over: true }); }} className="absolute bottom-14 right-2 inline-flex items-center gap-1 rounded-full bg-[#0B3F8C] px-2.5 py-1 text-[11px] font-bold text-white opacity-0 shadow transition group-hover:opacity-100" data-testid={`mlm-media-tovideo-${m.id}`}><Film className="h-3 w-3" />Vídeo</button>}
            </figure>
          ))}
        </div>
      </Card>
    </div>
  );
};
